import { FileDownloadOutlined } from "@mui/icons-material";
import { Button, Menu, MenuItem, Typography } from "@mui/material";
import {
  GridColDef,
  GridToolbarContainer,
  gridFilteredSortedRowEntriesSelector,
  useGridApiContext,
  useGridRootProps,
} from "@mui/x-data-grid";
import { useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "@/context/I18nProvider";
import { requestRaw } from "@/lib/axios";
import { buildCsv, downloadBlob } from "@/lib/export/csv";
import { fetchAllPages, findRecordedList } from "@/lib/export/listRecorder";
import { Cell, buildXlsx } from "@/lib/export/xlsx";
import { handleError } from "@/utils";

// Large enough for any report an admin opens; beyond it the export says it was cut.
const MAX_EXPORT_ROWS = 20000;
const SKIPPED_FIELDS = new Set(["__check__", "actions", "action", "__detail_panel_toggle__"]);

type Format = "csv" | "xlsx";

const toCell = (value: unknown): Cell => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") {
    return value;
  }
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => String(toCell(item))).join(", ");
  if (typeof value === "object") {
    const named = value as { name?: unknown; username?: unknown; label?: unknown };
    const label = named.name ?? named.username ?? named.label;
    if (typeof label === "string") return label;

    return JSON.stringify(value).slice(0, 2000);
  }

  return String(value);
};

const readPath = (row: object, field: string): unknown =>
  field
    .split(".")
    .reduce<unknown>((value, key) => (value as Record<string, unknown> | null)?.[key], row);

type GridApiRef = ReturnType<typeof useGridApiContext>;

const cellOf = (row: object, column: GridColDef, apiRef: GridApiRef): Cell => {
  try {
    let value = readPath(row, column.field);
    if (column.valueGetter) value = column.valueGetter(value as never, row, column, apiRef);
    if (column.valueFormatter) value = column.valueFormatter(value as never, row, column, apiRef);

    return toCell(value);
  } catch {
    return "";
  }
};

// The heading nearest to the grid (page title or dialog title) names the file.
const fileBaseName = (root: HTMLElement | null): string => {
  let node: HTMLElement | null = root;
  for (let depth = 0; node && depth < 8; depth++, node = node.parentElement) {
    const heading = node.querySelector("h5, h6, .MuiDialogTitle-root");
    const text = heading?.textContent?.trim();
    if (text)
      return text
        .replace(/[^\p{L}\p{N}]+/gu, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60);
  }

  return "export";
};

const stamp = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
};

/**
 * The toolbar every admin DataGrid gets (set in the theme): exports the table as CSV or XLSX.
 * A server-paged grid exports every page of its current query, not only the page on screen.
 */
const GridExportToolbar = () => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const exportAs = async (format: Format) => {
    setAnchor(null);
    try {
      setProgress(t("export.preparing"));
      const columns = apiRef.current
        .getVisibleColumns()
        .filter((column) => !SKIPPED_FIELDS.has(column.field) && column.type !== "actions");
      const shown = gridFilteredSortedRowEntriesSelector(apiRef).map((entry) => entry.model);

      let rows: object[] = shown;
      const serverPaged = rootProps.paginationMode === "server";
      if (serverPaged && (rootProps.rowCount ?? 0) > shown.length) {
        const list = findRecordedList(shown);
        if (list) {
          const all = await fetchAllPages(list, requestRaw, MAX_EXPORT_ROWS, (loaded, total) =>
            setProgress(t("export.progress", { loaded, total })),
          );
          rows = all.rows;
          if (all.total > rows.length) {
            toast.warning(t("export.truncated", { max: MAX_EXPORT_ROWS.toLocaleString() }));
          }
        } else {
          toast.info(t("export.pageOnly"));
        }
      }

      const header = columns.map((column) => column.headerName || column.field);
      const body = rows.map((row) => columns.map((column) => cellOf(row, column, apiRef)));
      const name = `${fileBaseName(apiRef.current.rootElementRef?.current ?? null)}-${stamp()}`;
      if (format === "csv") downloadBlob(buildCsv(header, body), `${name}.csv`);
      else downloadBlob(buildXlsx(header, body, name), `${name}.xlsx`);
    } catch (error) {
      handleError(error);
    } finally {
      setProgress(null);
    }
  };

  return (
    <GridToolbarContainer sx={{ justifyContent: "flex-end", px: 1, py: 0.5, gap: 1 }}>
      {progress && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {progress}
        </Typography>
      )}
      <Button
        size="small"
        startIcon={<FileDownloadOutlined />}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={progress !== null}
      >
        {t("export.button")}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => exportAs("csv")}>{t("export.csv")}</MenuItem>
        <MenuItem onClick={() => exportAs("xlsx")}>{t("export.xlsx")}</MenuItem>
      </Menu>
    </GridToolbarContainer>
  );
};

export default GridExportToolbar;
